import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface SeopEditContextValue {
  /** Há edição não salva na tela atual. */
  dirty: boolean;
  setDirty: (value: boolean) => void;
  /**
   * Registrado pela página: salva o que está pendente. O modal usa em
   * "Salvar e sair"; `null` quando a página não sabe salvar sozinha.
   */
  registerSave: (fn: (() => Promise<void>) | null) => void;
  save: (() => Promise<void>) | null;
}

const SeopEditContext = createContext<SeopEditContextValue | null>(null);

/**
 * Estado de edição pendente do módulo, para o layout enxergar a página filha.
 *
 * As abas do `SeopLayout` são `<Link>`, e o app monta `<BrowserRouter>` — o
 * `useBlocker` do react-router **lança** fora de um data router, então
 * interceptar a navegação exige o layout saber que a página está suja. O mês NÃO
 * entra aqui: ele vive na URL (`?mes=`), porque as rotas são planas e o layout
 * remonta a cada troca de aba.
 */
export const SeopEditProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dirty, setDirty] = useState(false);
  const [save, setSave] = useState<(() => Promise<void>) | null>(null);

  // `useState` com função exige o wrapper: `setSave(fn)` chamaria `fn` como updater.
  const registerSave = useCallback((fn: (() => Promise<void>) | null) => {
    setSave(() => fn);
  }, []);

  const value = useMemo(
    () => ({ dirty, setDirty, registerSave, save }),
    [dirty, registerSave, save],
  );

  return <SeopEditContext.Provider value={value}>{children}</SeopEditContext.Provider>;
};

/**
 * Fora do provider devolve um objeto inerte em vez de lançar: as páginas do
 * módulo são renderizadas isoladas em teste, e um `throw` aqui transformaria
 * "faltou um wrapper no teste" numa tela branca em produção.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSeopEdit(): SeopEditContextValue {
  const ctx = useContext(SeopEditContext);
  return (
    ctx ?? {
      dirty: false,
      setDirty: () => undefined,
      registerSave: () => undefined,
      save: null,
    }
  );
}

export default SeopEditContext;
